import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ticket,
  AlertCircle,
  Clock,
  User,
  Filter,
  Search,
  Flag,
  MessageSquare,
  Calendar,
  RefreshCw,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";

const UserAllocatedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const adminId = localStorage.getItem("adminId");
  const navigate = useNavigate();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      console.log("Fetching tickets for admin ID:", adminId);
      const response = await fetch(
        `${API_URL}/tickets/by-user?stage=assigned&user=${adminId}`
      );
      const data = await response.json();
      setTickets(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch tickets. Please try again later.");
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [adminId]);

  const handleTicketClick = (ticket) => {
    navigate(`/TicketManagement/${ticket.conversation_support_id}`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTickets();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getPriorityColor = (level) => {
    switch (level) {
      case 1:
        return "text-red-600 bg-red-50";
      case 2:
        return "text-orange-600 bg-orange-50";
      case 3:
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getPriorityText = (level) => {
    switch (level) {
      case 1:
        return "High Priority";
      case 2:
        return "Medium Priority";
      case 3:
        return "Low Priority";
      default:
        return "Unknown Priority";
    }
  };

  const filteredTickets = tickets.filter(
    (ticket) =>
      (ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (filterPriority === "all" ||
        ticket.priority_level === parseInt(filterPriority))
  );

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#002966]">
                Open Tickets
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and review assigned support tickets
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className={`p-2 rounded-full hover:bg-gray-100 transition-all ${
                refreshing ? "animate-spin" : ""
              }`}
            >
              <RefreshCw size={20} className="text-[#002966]" />
            </button>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ffc000]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  className="border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#ffc000]"
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                >
                  <option value="all">All Priorities</option>
                  <option value="1">High Priority</option>
                  <option value="2">Medium Priority</option>
                  <option value="3">Low Priority</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {error}
            </div>
          )}

          {/* Tickets Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002966] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading tickets...</p>
            </div>
          ) : (
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.conversation_support_id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-[#ffc000] flex items-center justify-center">
                          <User className="text-[#002966]" size={20} />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-[#002966]">
                            {ticket.customer_name}
                          </h2>
                          <span className="text-sm text-gray-500">
                            ID: #{ticket.conversation_support_id}
                          </span>
                        </div>
                      </div>
                      <div
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(
                          ticket.priority_level
                        )}`}
                      >
                        {getPriorityText(ticket.priority_level)}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <p className="text-gray-600 line-clamp-2">
                        {ticket.description}
                      </p>

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock size={16} className="mr-1" />
                          <span>
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <MessageSquare size={16} className="mr-1" />
                          <span>{ticket.conversation_stage}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleTicketClick(ticket)}
                      className="mt-4 w-full bg-[#002966] text-white py-2 rounded-lg hover:bg-[#001f4d] transition-colors duration-300 flex items-center justify-center"
                    >
                      <Ticket size={18} className="mr-2" />
                      Handle Ticket
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredTickets.length === 0 && (
            <div className="text-center py-12">
              <Ticket size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">
                No tickets found
              </h3>
              <p className="text-gray-500 mt-2">
                No tickets match your current filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserAllocatedTickets;
